import { CALL_HARD_ERRORS, type CallHardError } from '@stores/call/callNotices';
import { str_pad_left } from '@helpers/functions';
import { isBrowserVersionAcceptable } from '@helpers/getBrowserDetails';
import { logout } from '@composables/session';
import { getRouter } from '@src/const/routerHandle';
import { peerJsOptions } from '@src/const/peerConfigConsts';
import { useAuthStore } from '@stores/authStore';
import { useCallAudioStore } from '@stores/call/callAudioStore';
import { useCallChatStore } from '@stores/call/callChatStore';
import { CallEventType, useCallEventStore } from '@stores/call/callEventStore';
import { useCallReconnectStore } from '@stores/call/callReconnectStore';
import { useCallSocketStore } from '@stores/call/callSocketStore';
import { type CallMetaData } from '@stores/call/callTypes';
import { useDataConnectionsStore } from '@stores/call/dataConnectionsStore';
import { useLocalMediaStore } from '@stores/call/localMediaStore';
import { useMediaConnectionsStore } from '@stores/call/mediaConnectionsStore';
import { useModalStore } from '@stores/modalStore';
import { useQueryClient } from '@tanstack/vue-query';
import { callsMutations, usersQueries, usersKeys } from '@therame/api-client';
import { CallRequest, getPath, Role, UserFullSizeDto, UserProfileDto } from '@therame/shared';
import dayjs from 'dayjs';
import Peer, { PeerErrorType } from 'peerjs';
import { defineStore } from 'pinia';
import { Subject, Subscription } from 'rxjs';
import { computed, ref, type ComputedRef, type Ref } from 'vue';

type SafeSubject<T> = Omit<Subject<T>, 'currentObservers'>;

type CallStore = {
  connectionText: Ref<string>;
  peerJsServer: Ref<any>;
  callSecondsText: ComputedRef<string>;
  chatShown: Ref<boolean>;
  documentDragTrigger: Ref<SafeSubject<DragEvent>>;
  callUpdateTrigger: Ref<SafeSubject<void>>;
  connectedIds: ComputedRef<string[]>;
  stopPeerConnections: () => Promise<void>;
  callId: Ref<string | null>;
  startPeerConnection: () => Promise<boolean>;
  callDurationTimerInterval: Ref<ReturnType<typeof setInterval> | undefined>;
  startCallTimer: () => void;
  initializationTime: Ref<Date | null>;
  hangUp: () => Promise<void>;
  /** Ask before ending the consultation, then hang up. */
  confirmHangUp: () => Promise<void>;
  /** Ask before removing a participant, then kick them from the call. */
  confirmKickUser: (patient: UserFullSizeDto) => Promise<void>;
  initCall: (newCallId: string) => Promise<void>;
  setInitializationTime: () => Promise<void>;
  connectPeer: (userId: string) => Promise<boolean>;
  disconnectPeer: (userId: string) => void;
  callActive: ComputedRef<boolean>;
  logger: Console;
  /**
   * Tear the call down and tell the user why, then send them back to the
   * waiting room once they have acknowledged it.
   */
  stopCallOnHardError: (reason: CallHardError) => Promise<void>;
  addProfileToCache: (userId: string) => Promise<UserProfileDto>;
  initSubscriptions: () => void;
  connecting: Ref<boolean>;
};

export const useCallStore = defineStore('call', (): CallStore => {
  const authStore = useAuthStore();
  const modalStore = useModalStore();
  const callSocketStore = useCallSocketStore();
  const callAudioStore = useCallAudioStore();
  const localMediaStore = useLocalMediaStore();
  const dataConnectionsStore = useDataConnectionsStore();
  const mediaConnectionsStore = useMediaConnectionsStore();
  const callEventStore = useCallEventStore();
  const callReconnectStore = useCallReconnectStore();
  const logger = console;
  const callChatStore = useCallChatStore();
  const queryClient = useQueryClient();
  const joiningViaSocket = ref<boolean>(false);

  /**
   * refers to the callId of the current call, if null, no call is active
   */
  const callId = ref<string | null>(null);
  const callActive = computed(() => callId.value !== null);
  const connecting = ref<boolean>(false);

  const peerJsServer = ref<InstanceType<typeof Peer> | undefined>(undefined);

  // call-duration
  const initializationTime = ref<Date | null>(null);
  const callDurationTimerInterval = ref<ReturnType<typeof setInterval>>();
  const callSeconds = ref<number>(0);

  // ui-elements
  const chatShown = ref<boolean>(true);
  const connectionText = ref<string>('verbinde...');
  const callSecondsText = computed(() => {
    const minutes = Math.floor(callSeconds.value / 60);
    const seconds = callSeconds.value % 60;
    return str_pad_left(minutes, '0', 2) + ':' + str_pad_left(seconds, '0', 2);
  });

  // trigger
  const callUpdateTrigger = ref<SafeSubject<void>>(new Subject<void>());
  const documentDragTrigger = ref<SafeSubject<DragEvent>>(new Subject<DragEvent>());

  /**
   * socket.io-subscriptions
   */
  const joinSubscription = ref<Subscription>();
  const leaveSubscription = ref<Subscription>();
  const disconnectSubscription = ref<Subscription>();
  const callEndedByTherapistSubscription = ref<Subscription>();
  const callUserGotKickedSubscription = ref<Subscription>();

  const connectedIds = computed(() => {
    return [...dataConnectionsStore.dataConnections.keys()];
  });

  /** creates new peer to listen for data and calls */
  const startPeerConnection = () => {
    return Promise.race<boolean>([
      new Promise((resolve) => {
        setTimeout(() => {
          resolve(false);
        }, 10000);
      }),
      new Promise<boolean>((resolve) => {
        // never overwrite existing peer
        if (peerJsServer.value) {
          return;
        }

        if (!authStore.authContext?.user?.id) {
          stopCallOnHardError('connection');
          return;
        }

        /**
         * create connection to peerjs-server
         */
        peerJsServer.value = new Peer(authStore.authContext.user.id, peerJsOptions);

        /**
         * received new p2p-data-connection
         */
        peerJsServer.value.on('connection', async (dataConnection) => {
          callEventStore.emit(CallEventType.PEER_JS, 'connection', {
            dataConnection: {
              peer: dataConnection.peer,
              label: dataConnection.label,
            },
          });
          if (!dataConnectionsStore.dataConnections.has(dataConnection.peer)) {
            dataConnectionsStore.storeNewDataConnection(dataConnection);
          }
          dataConnectionsStore.addDataConnectionListeners(dataConnection);
        });

        /**
         * received new p2p-media-connection
         * - incoming calls are always saved and answered with localStream
         */
        peerJsServer.value.on('call', async (mediaConnection) => {
          callEventStore.emit(CallEventType.PEER_JS, 'call', {
            mediaConnection: {
              peer: mediaConnection.peer,
              label: mediaConnection.label,
            },
          });
          if (!localMediaStore.localStream) {
            return;
          }
          await mediaConnectionsStore.receiveMediaConnection(
            mediaConnection,
            localMediaStore.localStream,
          ); // always to be saved
        });

        /**
         * disconnected from peerJS-server
         */
        peerJsServer.value.on('disconnected', async () => {
          callEventStore.emit(CallEventType.PEER_JS, 'disconnected');
          callReconnectStore.startReconnectingToPeerJS();
        });

        /**
         * handle peerJS errors
         */
        peerJsServer.value.on('error', async (error: any) => {
          callEventStore.emit(CallEventType.PEER_JS, 'error', { error });

          switch (error.type) {
            case PeerErrorType.BrowserIncompatible:
              stopCallOnHardError('browserVersion');
              break;
            case PeerErrorType.Disconnected:
              callReconnectStore.startReconnectingToPeerJS();
              break;
            case PeerErrorType.InvalidID:
              stopCallOnHardError('connection');
              break;
            case PeerErrorType.InvalidKey:
              stopCallOnHardError('connection');
              break;
            case PeerErrorType.Network:
              callReconnectStore.startReconnectingToPeerJS();
              break;
            case PeerErrorType.PeerUnavailable:
              // splitting hard-coded-message: `Could not connect to peer ${peerId}`
              const splitErrorMsg = error.message.split(' ');

              if (splitErrorMsg?.length !== 5) {
                stopCallOnHardError('connection');
                break;
              }
              const peerId = splitErrorMsg[5]; // workaround to get peerId
              callReconnectStore.startReconnectingToPeer(peerId);
              break;
            case PeerErrorType.SslUnavailable:
              stopCallOnHardError('connection');
              break;
            case PeerErrorType.ServerError:
              stopCallOnHardError('connection');
              break;
            case PeerErrorType.SocketError:
              callReconnectStore.startReconnectingToPeerJS();
              break;
            case PeerErrorType.SocketClosed:
              callReconnectStore.startReconnectingToPeerJS();
              break;
            case PeerErrorType.UnavailableID:
              stopCallOnHardError('noTwoCalls');
              break;
            case PeerErrorType.WebRTC:
              logger.error('webtRTC-error: ', error);
              // TODO: do nothing for now
              break;
            default:
              stopCallOnHardError('connection');
              break;
          }
        });

        /**
         * connection to peerJS successful.
         * awaiting connections to other peer
         */
        peerJsServer.value.on('open', async (id) => {
          callEventStore.emit(CallEventType.PEER_JS, 'open', { id });
          connectionText.value = 'Warte auf Teilnehmer:innen...';
          callAudioStore.playAudio(callAudioStore.selfConnectAudio);
          resolve(true);
        });
      }),
    ]);
  };

  /** stops peer and clears all state-values */
  const stopPeerConnections = async () => {
    dataConnectionsStore.dataConnections.forEach((dataConnection) => {
      dataConnection.close();
    });
    dataConnectionsStore.remoteCallMetadata = new Map<string, CallMetaData>();
    mediaConnectionsStore.mediaConnections.forEach((mediaConnection) => {
      mediaConnection.close();
    });
    mediaConnectionsStore.focusedRemoteStream = undefined;
    mediaConnectionsStore.focusedUserId = undefined;
    mediaConnectionsStore.lockedFocusUserId = undefined;
    peerJsServer.value?.removeAllListeners();
    peerJsServer.value?.destroy();
    peerJsServer.value = undefined;
    callAudioStore.playAudio(callAudioStore.selfDisconnectAudio);
  };

  /** starts timer to track call-duration */
  const startCallTimer = () => {
    callSeconds.value = initializationTime.value
      ? dayjs().diff(dayjs(initializationTime.value), 'seconds')
      : 0;

    callDurationTimerInterval.value = setInterval(() => {
      callSeconds.value++;
    }, 1000);
    callSeconds.value++;
  };

  /** connects to one peer */
  const connectPeer = async (userId: string) => {
    return Promise.race<boolean>([
      new Promise((resolve) => {
        setTimeout(() => {
          resolve(false);
        }, 10000);
      }),
      new Promise((resolve) => {
        (async () => {
          if (!peerJsServer.value) {
            await stopCallOnHardError('connection');
            resolve(false);
            return;
          }

          if (!localMediaStore.localStream) {
            await stopCallOnHardError('media');
            resolve(false);
            return;
          }

          // connect data if user not already connected and not self
          if (
            !dataConnectionsStore.dataConnections.has(userId) &&
            userId !== authStore.authContext?.user?.id
          ) {
            await dataConnectionsStore.createNewDataConnection(peerJsServer.value as Peer, userId);
          }

          // connect media
          if (
            !mediaConnectionsStore.mediaConnections.has(userId) &&
            userId !== authStore.authContext?.user?.id
          ) {
            await mediaConnectionsStore.createNewMediaConnection(
              peerJsServer.value as Peer,
              userId,
              localMediaStore.localStream,
            );
          }
          resolve(true);
        })();
      }),
    ]);
  };

  const disconnectPeer = (userId: string) => {
    // data-connections
    if (dataConnectionsStore.dataConnections.has(userId)) {
      dataConnectionsStore.dataConnections.get(userId)?.close();
      dataConnectionsStore.dataConnections.delete(userId);
      dataConnectionsStore.remoteCallMetadata.delete(userId);
    }

    // media-connections
    if (mediaConnectionsStore.mediaConnections.has(userId)) {
      mediaConnectionsStore.remoteStreamsMap
        .get(userId)
        ?.getTracks()
        .forEach((track) => track.stop());
      mediaConnectionsStore.remoteStreamsMap.delete(userId);
      mediaConnectionsStore.remoteStreamSourcesMap.get(userId)?.disconnect();
      mediaConnectionsStore.remoteStreamSourcesMap.delete(userId);
      mediaConnectionsStore.mediaConnections.get(userId)?.close();
      mediaConnectionsStore.mediaConnections.delete(userId);
      mediaConnectionsStore.updateFocusedStreamOnClose(userId);
    }
  };

  const joinCall = async (callId: string) => {
    initSubscriptions();
    callChatStore.clearMessages();
    joiningViaSocket.value = true;
    try {
      const response = await callSocketStore.join(callId);
      if (response.statusCode === 404) {
        stopCallOnHardError('notFound');
        return;
      }
      if (response.statusCode !== 200) {
        throw new Error('Could not join call. Reason: ' + response.message);
      }
      callSocketStore.callIdInServerStatus = response.body?.callId || null;
      // Requests already waiting when this tab joins, e.g. the one from another
      // office that opened the call in this tab (#1837).
      for (const pending of response.body?.pendingRequests ?? []) {
        if (pending.isApproved || !pending.user.id) continue;
        const callRequest = new CallRequest();
        callRequest.user = pending.user;
        callRequest.callId = callId;
        callSocketStore.callRequests.set(pending.user.id + '', callRequest);
      }
      await setInitializationTime();
      // if first connection, start call-timer
      if (!dataConnectionsStore.dataConnectionTime || !callDurationTimerInterval.value) {
        // TODO: let server send the time
        dataConnectionsStore.dataConnectionTime = new Date();
        startCallTimer();
      }
      const users = response.body?.connectedUsers || [];
      for (const user of users) {
        if (!user.id) {
          return;
        }
        queryClient.setQueryData(usersKeys.profile(user.id), user);
        await connectPeer(user.id);
        const localMeta = dataConnectionsStore.remoteCallMetadata.get(user.id);

        if (!localMeta) {
          return;
        }
        localMeta.displayName = user.firstName + ' ' + user.lastName;
        dataConnectionsStore.remoteCallMetadata.set(user.id, localMeta);
      }
    } catch (err) {
      logger.error(err);
      stopCallOnHardError('connection');
    } finally {
      joiningViaSocket.value = false;
    }
  };

  const setInitializationTime = async () => {
    const call = await callsMutations.init(callId.value!);
    initializationTime.value = call.initializationTime;
  };

  const initCall = async (newCallId: string) => {
    if (connecting.value) {
      return;
    }
    connecting.value = true;

    if (
      authStore.authContext?.usedForm === 'tan' &&
      authStore.authContext?.tanCallId !== newCallId
    ) {
      logout();
      return;
    }

    if (!isBrowserVersionAcceptable()) {
      stopCallOnHardError('browserVersion');
      return;
    }

    if (
      !!callSocketStore.callIdInServerStatus &&
      callSocketStore.callIdInServerStatus !== newCallId
    ) {
      void modalStore.notifyAndRun({
        ...CALL_HARD_ERRORS.noTwoCalls,
        action: () => getRouter().push(getPath<'callWaitingRoom'>('/call/waiting-room', {})),
      });
      return;
    }
    const joiningNewCall = callId.value !== newCallId;
    callId.value = newCallId;
    callSocketStore.callIdInServerStatus = newCallId;

    if (joiningNewCall) {
      await localMediaStore.initLocalMedia();
      await startPeerConnection();
      await joinCall(callId.value);
    }
    connecting.value = false;
  };

  window.addEventListener('offline', async () => {
    if (callId.value) {
      await stopPeerConnections();
      await callSocketStore.disconnect(callId.value);
    }
  });

  window.addEventListener('online', () => {
    setTimeout(async () => {
      if (callId.value) {
        await startPeerConnection();
        await joinCall(callId.value);
      }
    }, 1000);
  });

  const initSubscriptions = () => {
    callChatStore.subscribeNewIncommingMsg();

    if (joinSubscription.value && !joinSubscription.value.closed) {
      joinSubscription.value.unsubscribe();
    }
    joinSubscription.value = callSocketStore.joinTrigger.subscribe((event) => {
      if (event.props.user.id !== authStore.authContext?.user?.id) {
        // connectPeer(event.props.user.id + ""); => wrong here, because the connecting user uses peer.call() which we answer in on("call") listener
      }
    });

    if (leaveSubscription.value && !leaveSubscription.value.closed) {
      leaveSubscription.value.unsubscribe();
    }
    leaveSubscription.value = callSocketStore.leaveTrigger.subscribe(async (event) => {
      const userId = event.props.user?.id;
      if (!userId) {
        return;
      }
      disconnectPeer(userId);
    });

    if (callEndedByTherapistSubscription.value && !callEndedByTherapistSubscription.value.closed) {
      callEndedByTherapistSubscription.value.unsubscribe();
    }
    callEndedByTherapistSubscription.value = callSocketStore.callEndedByTherapistTrigger.subscribe(
      async () => {
        await hangUp();
        void modalStore.notify({
          header: 'Anruf beendet',
          text: 'Der Anruf wurde von deinem Therapeuten beendet.',
        });
      },
    );

    if (callUserGotKickedSubscription.value && !callUserGotKickedSubscription.value.closed) {
      callUserGotKickedSubscription.value.unsubscribe();
    }
    callUserGotKickedSubscription.value = callSocketStore.callUserGotKickedTrigger.subscribe(
      async (event) => {
        if (event.props.userId === authStore.authContext?.user?.id) {
          await hangUp();
          void modalStore.notify({
            header: 'Information',
            text: 'Der Anruf wurde beendet oder du wurdest aus dem Anruf entfernt.',
          });
        } else {
          disconnectPeer(event.props.userId);
        }
      },
    );

    if (disconnectSubscription.value && !disconnectSubscription.value.closed) {
      disconnectSubscription.value.unsubscribe();
    }
    disconnectSubscription.value = callSocketStore.callDisconnectTrigger.subscribe((event) => {
      const userId = event.props.user?.id;
      if (!userId) {
        return;
      }
      disconnectPeer(userId);
    });
  };

  const hangUp = async () => {
    // The teardown below talks to the socket (leave/status), which can reject
    // when the socket is already gone or an ack times out. Those failures must
    // not swallow the redirect off the call view — otherwise the call ends but
    // the user is stranded on /consultation/:callId. Run teardown in try, and
    // always navigate in finally.
    try {
      callReconnectStore.reset();
      // The mini toolbar can show a hang-up button while there is no local call
      // session (callId is null) but the server still has us in the call
      // (callIdInServerStatus set) — e.g. after navigating away or a reload. In
      // that state we must still send `leave` against the server-known id,
      // otherwise the server keeps us in the call, status() re-reads it, and the
      // mini toolbar never goes away (the button appears to do nothing).
      const idToLeave = callId.value ?? callSocketStore.callIdInServerStatus;
      if (idToLeave) {
        // A dead socket or timed-out ack must not swallow the local teardown
        // below — otherwise we navigate away while camera/mic tracks and peer
        // connections stay live. Swallow the socket failure and press on.
        try {
          await callSocketStore.leave(idToLeave);
        } catch (err) {
          logger.error('Failed to leave call socket during hangUp', err);
        }
        callId.value = null;
      }
      dataConnectionsStore.dataConnectionTime = undefined;
      callChatStore.unsubscribeNewIncomingMsg();
      await stopPeerConnections();
      await localMediaStore.stopLocalMedia();
      // status() re-reads the socket and can reject too; local teardown has
      // already happened, so a failure here is non-fatal.
      try {
        await callSocketStore.status();
      } catch (err) {
        logger.error('Failed to refresh call socket status during hangUp', err);
      }
    } finally {
      const router = getRouter();
      if (router.currentRoute.value.name === 'videoCall') {
        if (authStore.authContext?.user?.role === Role.PATIENT) {
          router.push(getPath<'home'>('/', {}));
        } else {
          router.push(getPath<'callWaitingRoom'>('/call/waiting-room', {}));
        }
      }
    }
  };

  // The two confirmations the call UI raises. They live here rather than in the
  // toolbars because the action they guard does too — and both toolbars plus the
  // participant list share them.
  const confirmHangUp = async () => {
    const confirmed = await modalStore.confirm({
      header: 'Beenden?',
      text: 'Möchtest du die Sprechstunde wirklich beenden?',
      confirmText: 'Beenden',
      confirmIcon: 'icon-[mingcute--phone-off-line]',
    });
    if (confirmed) {
      await hangUp();
    }
  };

  const confirmKickUser = async (patient: UserFullSizeDto) => {
    const confirmed = await modalStore.confirm({
      header: 'Entfernen?',
      text: `Möchtest du ${patient.firstName} ${patient.lastName} wirklich aus dieser Sprechstunde entfernen?`,
      confirmText: 'Entfernen',
    });
    // Re-read callId after the await: the call can have ended while the
    // dialogue was open.
    if (confirmed && callId.value) {
      await callSocketStore.kickUser(patient.id, callId.value);
    }
  };

  const stopCallOnHardError = async (reason: CallHardError) => {
    await hangUp();
    // Not awaited: callers await this for "the teardown is done", and hanging
    // that on a click would make them wait on a human. The redirect rides along
    // as the notice's follow-up so it lands after the dialogue's own
    // history.back(), not before it.
    void modalStore.notifyAndRun({
      ...CALL_HARD_ERRORS[reason],
      action: () => getRouter().push(getPath<'callWaitingRoom'>('/call/waiting-room', {})),
    });
  };

  const addProfileToCache = async (userId: string): Promise<UserProfileDto> => {
    return queryClient.fetchQuery({
      queryKey: usersKeys.profile(userId),
      queryFn: () => usersQueries.getProfile(userId),
    });
  };

  const store: CallStore = {
    connectionText,
    peerJsServer,
    callSecondsText,
    chatShown,
    documentDragTrigger,
    callUpdateTrigger,
    connectedIds,
    stopPeerConnections,
    callId,
    startPeerConnection,
    callDurationTimerInterval,
    startCallTimer,
    initializationTime,
    hangUp,
    confirmHangUp,
    confirmKickUser,
    initCall,
    setInitializationTime,
    connectPeer,
    disconnectPeer,
    callActive,
    logger,
    stopCallOnHardError,
    addProfileToCache,
    initSubscriptions,
    connecting,
  };
  return store;
});
